import { authenticatedFetch } from "@lumi/data";
import { useCallback, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import type { SubtitleMetadata } from "./useContentEditor";
import type { Submission } from "./useSubmissions";

/**
 * Crew member with role
 */
export interface CrewMember {
  name: string;
  job: string;
  department?: string;
}

/**
 * Create submission request
 */
export interface CreateSubmissionRequest {
  title: string;
  releaseYear?: number;
  runtime?: number;
  synopsis?: string;
  description?: string;
  director?: string;
  cast?: string[];
  crew?: CrewMember[];
  genres?: string[];
  rating?: string;
  languages?: string[];
  country?: string;
  tmdbId?: number;
  posterUrl?: string;
  backdropUrl?: string;
  logoUrl?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];
}

/**
 * Update submission request
 */
export interface UpdateSubmissionRequest {
  title?: string;
  releaseYear?: number;
  runtime?: number;
  synopsis?: string;
  description?: string;
  director?: string;
  cast?: string[];
  crew?: CrewMember[];
  genres?: string[];
  rating?: string;
  languages?: string[];
  country?: string;
  tmdbId?: number;
  posterUrl?: string;
  backdropUrl?: string;
  logoUrl?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];
}

/**
 * Hook for submission mutation operations
 */
/* eslint-disable max-lines-per-function */
// NOTE: Mutations hook with 5 API operations (create, update, delete, submit, resubmit)
export function useSubmissionMutations() {
  const { accessToken, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  /**
   * Create a new submission (DRAFT status)
   */
  const createSubmission = useCallback(
    async (data: CreateSubmissionRequest): Promise<Submission | null> => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      try {
        setLoading(true);
        setError(null);

        // Transform frontend data to backend API format
        const payload = {
          title: data.title,
          synopsis: data.synopsis,
          description: data.description,
          year: data.releaseYear,
          country: data.country,
          languages: data.languages || [],
          durationMinutes: data.runtime,
          rating: data.rating,
          directorData: data.director ? [{ name: data.director }] : undefined,
          castData: data.cast?.map((name) => ({ name })),
          crewData: data.crew,
          genreData: data.genres,
          posterUrl: data.posterUrl,
          backdropUrl: data.backdropUrl,
          logoUrl: data.logoUrl,
          horizontalTileUrl: data.horizontalTileUrl,
          verticalTileUrl: data.verticalTileUrl,
          tmdbId: data.tmdbId,
          // Store subtitle metadata if present
          metadata:
            data.featureSubtitles || data.trailerSubtitles
              ? {
                  featureSubtitles: data.featureSubtitles,
                  trailerSubtitles: data.trailerSubtitles,
                }
              : undefined,
        };

        const result = await authenticatedFetch<{ submission: Submission }>(
          `${baseUrl}/v1/provider/submissions`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        return result.submission;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshAuth, baseUrl],
  );

  /**
   * Update an existing submission
   */
  const updateSubmission = async (id: string, data: UpdateSubmissionRequest): Promise<Submission | null> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      setLoading(true);
      setError(null);

      // Transform frontend data to backend API format (flat structure for PATCH)
      const payload = {
        title: data.title,
        synopsis: data.synopsis,
        description: data.description,
        year: data.releaseYear,
        country: data.country,
        languages: data.languages,
        durationMinutes: data.runtime,
        rating: data.rating,
        directorData: data.director ? [{ name: data.director }] : undefined,
        castData: data.cast?.map((name) => ({ name })),
        crewData: data.crew,
        genreData: data.genres,
        posterUrl: data.posterUrl,
        backdropUrl: data.backdropUrl,
        logoUrl: data.logoUrl,
        horizontalTileUrl: data.horizontalTileUrl,
        verticalTileUrl: data.verticalTileUrl,
        tmdbId: data.tmdbId,
        // Store the full TMDB data for reference if tmdbId is present
        tmdbData: data.tmdbId
          ? {
              id: data.tmdbId,
              title: data.title,
              overview: data.synopsis,
              release_date: data.releaseYear ? `${data.releaseYear}-01-01` : undefined,
              runtime: data.runtime,
              poster_path: data.posterUrl,
              backdrop_path: data.backdropUrl,
              original_language: data.languages?.[0],
              production_countries: data.country ? [{ iso_3166_1: data.country }] : undefined,
            }
          : undefined,
        // Store subtitle metadata if present
        metadata:
          data.featureSubtitles || data.trailerSubtitles
            ? {
                featureSubtitles: data.featureSubtitles,
                trailerSubtitles: data.trailerSubtitles,
              }
            : undefined,
      };

      console.log("Updating submission with payload:", payload);
      console.log("Genres from data:", data.genres);

      const result = await authenticatedFetch<{ submission: Submission }>(
        `${baseUrl}/v1/provider/submissions/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return result.submission;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a submission (only DRAFT status allowed)
   */
  const deleteSubmission = async (id: string): Promise<void> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      setLoading(true);
      setError(null);

      await authenticatedFetch<{ ok: boolean }>(
        `${baseUrl}/v1/provider/submissions/${id}`,
        {
          method: "DELETE",
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submit for review (DRAFT → PENDING)
   */
  const submitForReview = async (id: string): Promise<Submission | null> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      setLoading(true);
      setError(null);

      const result = await authenticatedFetch<{ submission: Submission }>(
        `${baseUrl}/v1/provider/submissions/${id}/submit`,
        {
          method: "POST",
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return result.submission;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resubmit after rejection (REJECTED → PENDING)
   */
  const resubmit = async (id: string): Promise<Submission | null> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      setLoading(true);
      setError(null);

      const result = await authenticatedFetch<{ submission: Submission }>(
        `${baseUrl}/v1/provider/submissions/${id}/resubmit`,
        {
          method: "POST",
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return result.submission;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Withdraw a pending submission (PENDING → DRAFT)
   */
  const withdrawSubmission = async (id: string, reason?: string): Promise<Submission | null> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      setLoading(true);
      setError(null);

      const result = await authenticatedFetch<{ submission: Submission }>(
        `${baseUrl}/v1/provider/submissions/${id}/withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return result.submission;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createSubmission,
    updateSubmission,
    deleteSubmission,
    submitForReview,
    resubmit,
    withdrawSubmission,
  };
}
